import { useEffect } from "react";
// import * as Icons from "lucide-react";
import Heading from "@/components/tpr/Heading/Heading";
import { BreadcrumbItem, BreadcrumbLink } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useBreadcrumb } from "@/contexts/breadcrumbContext";
import { useHelpCategory } from "@/hooks/useHelpContent";
import { ArrowLeft, FileText } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const HelpCategoryView = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { setBreadcrumb } = useBreadcrumb();
  const { data, isLoading, error } = useHelpCategory(slug!);

  useEffect(() => {
    if (data) {
      setBreadcrumb(
        <>
          <BreadcrumbItem className="text-sm text-gray-600 truncate max-w-[125px] md:max-w-none">
            <BreadcrumbLink href="/help">Help &amp; Support</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem className="text-sm text-gray-600 truncate max-w-[125px] md:max-w-none">
            <BreadcrumbLink href="#">{data.category.name}</BreadcrumbLink>
          </BreadcrumbItem>
        </>,
      );
    }
    return () => setBreadcrumb(null);
  }, [setBreadcrumb, data]);

  if (isLoading) {
    return (
      <div className="px-4 min-h-[80vh]">
        <div className="mb-8 mt-8">
          <Skeleton className="h-10 w-64 mb-4" />
          <Skeleton className="h-6 w-96" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="px-4 min-h-[80vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Category Not Found</h2>
          <p className="text-gray-600 mb-6">The help category you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate("/help")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Help
          </Button>
        </div>
      </div>
    );
  }

  const { category, articles } = data;
  // const IconComponent = (Icons as any)[category.icon] || Icons.HelpCircle;

  return (
    <div className="px-4 min-h-[80vh]">
      <div className="mb-8 mt-8">
        <Button variant="ghost" onClick={() => navigate("/help")} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Help
        </Button>

        <div className={`${category.gradient} p-8 rounded-lg mb-8`}>
          <div className="flex items-center gap-4 mb-4">
            {/* <IconComponent className="h-12 w-12" /> */}
            <Heading title={category.name} />
          </div>
          {/* <p className="text-lg">{category.description}</p> */}
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-semibold mb-6">
          {articles.length} {articles.length === 1 ? "Article" : "Articles"}
        </h2>

        {articles.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-md">
            <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No articles available in this category yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {articles.map((article) => (
              <div
                key={article.id}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/help/article/${article.slug}`)}
              >
                <div className="flex items-start gap-4">
                  <FileText className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">{article.title}</h3>
                    <p className="text-gray-600 mb-3">{article.summary}</p>

                    {article.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {article.tags.map((tag) => (
                          <span key={tag} className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {article.viewCount > 0 && (
                    <div className="text-sm text-gray-500">
                      {article.viewCount} {article.viewCount === 1 ? "view" : "views"}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpCategoryView;
